import {
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Link as MuiLink,
  Typography,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { KeyedMutator } from 'swr/_internal';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { AccountData, AccountService, PassOrderPageData, UpdateAccountGeneralInput } from '../api';
import { FormCheckbox } from '../experienceSettings/formComponents/FormCheckBox';
import { FormTextField } from '../experienceSettings/formComponents/FormTextField';
import { getDateFormat, getTimeFormat } from '../common/getDateAndTimeFormat';
import { OrderPagesMultiSelect } from '../userSettings/OrderPagesMultiSelect';
import { getOrderPages, getPassOrderPages } from '../utils/AccountUtils';
import { useLanguage } from '../common/GeneralUtils';

type AccountGeneralSettingsProps = {
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (value: boolean) => void;
};
export const AccountGeneralSettings = ({
  account,
  updateLocalAccount,
  setIsUpdatedSnackOpen,
}: AccountGeneralSettingsProps) => {
  const { longDateFormat } = getDateFormat(account);
  const { t } = useTranslation();
  const language = useLanguage();
  const [isUpdatingSettings, setIsUpdatingSettings] = React.useState(false);
  const [applicableOrderPagesOpen, setApplicableOrderPagesOpen] = React.useState(false);
  const passOrderPages = getPassOrderPages(account);
  const orderPagesCount = getOrderPages(account)?.length ?? 0;

  type FormValues = UpdateAccountGeneralInput & { discount_applies_to_passes: boolean };

  const savedPassIds = account?.returning_customer_settings?.applicable_pass_order_page_ids;
  const initialAppliesToPasses = !savedPassIds || savedPassIds.length > 0;

  const initialValues: FormValues = {
    should_send_waiting_list_notifications: account?.should_send_waiting_list_notifications || false,
    returning_customer_discount_enabled: !!account?.returning_customer_settings,
    returning_customer_discount_percentage: account?.returning_customer_settings?.value || 20,
    returning_customer_discount_days: account?.returning_customer_settings?.valid_days || 14,
    returning_customer_discount_applicable_order_page_ids:
      account?.returning_customer_settings?.applicable_order_page_ids || [],
    returning_customer_discount_applicable_pass_order_page_ids: savedPassIds || [],
    discount_applies_to_passes: initialAppliesToPasses,
    should_send_abandoned_cart_notifications: account?.should_send_abandoned_cart_notifications || false,
    external_vouchers_enabled: account?.external_vouchers_enabled || false,
  };

  const validationSchema = yup.object().shape({
    should_send_waiting_list_notifications: yup.boolean().required(),
    external_vouchers_enabled: yup.boolean().required(),
    returning_customer_discount_enabled: yup.boolean().required(),
    returning_customer_discount_percentage: yup.number().when('returning_customer_discount_enabled', {
      is: true,
      then: yup.number().required().min(0).max(100),
    }),
    returning_customer_discount_days: yup.number().when('returning_customer_discount_enabled', {
      is: true,
      then: yup.number().required(),
    }),
  });

  const onSubmit = async (values: FormValues) => {
    setIsUpdatingSettings(true);
    const { discount_applies_to_passes, ...rest } = values;
    const payload: UpdateAccountGeneralInput = { ...rest };
    if (payload.returning_customer_discount_applicable_order_page_ids?.length === 0) {
      delete payload.returning_customer_discount_applicable_order_page_ids;
    }
    if (!discount_applies_to_passes) {
      payload.returning_customer_discount_applicable_pass_order_page_ids = [];
    } else if (payload.returning_customer_discount_applicable_pass_order_page_ids?.length === 0) {
      delete payload.returning_customer_discount_applicable_pass_order_page_ids;
    }
    const updatedAccount = await AccountService.generalSettings(payload);
    await updateLocalAccount(updatedAccount);
    setIsUpdatingSettings(false);
    setIsUpdatedSnackOpen(true);
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ handleSubmit, values, errors, touched, handleChange }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container flexDirection="column">
            <Grid container flexDirection="column">
              <Grid item mb={2}>
                <Typography variant="h4">{t('accountSettings.generaltitle')}</Typography>
              </Grid>
              <Grid item>
                <FormCheckbox
                  fieldName={'should_send_waiting_list_notifications'}
                  disabled={false}
                  isFieldUpdate
                  label={t('accountSettings.shouldSendWaitlist')}
                />
              </Grid>
              <Grid item my={3}>
                <Divider />
              </Grid>
              <Grid item>
                <Typography variant="h4">{t('accountSettings.returningcustomer')}</Typography>
              </Grid>
              <Grid item mb={2}>
                <Typography variant="body1" color="text.secondary">
                  {t('accountSettings.returningcustomercaption')}
                </Typography>
              </Grid>
              <Grid item>
                <FormCheckbox
                  fieldName={'returning_customer_discount_enabled'}
                  disabled={false}
                  isFieldUpdate
                  label={t('accountSettings.enableReturningCustomerDiscount')}
                />
              </Grid>
              <Collapse in={!!values.returning_customer_discount_enabled} unmountOnExit>
                <Grid container flexDirection="column" mt={1}>
                  <Grid container gap={1} flexWrap="nowrap" alignItems="center" mb={1}>
                    <Grid>
                      <Typography>{t('accountSettings.returningCustomerDiscountPercentage')}</Typography>
                    </Grid>
                    <Grid>
                      <FormTextField
                        width={80}
                        endAdornment="%"
                        type="number"
                        fieldName={'returning_customer_discount_percentage'}
                        isFieldUpdate
                      />
                    </Grid>
                  </Grid>
                  <Grid container gap={1} flexWrap="nowrap" alignItems="center">
                    <Grid>
                      <Typography>{t('accountSettings.returningCustomerDiscountDays')}</Typography>
                    </Grid>
                    <Grid>
                      <FormTextField
                        width={100}
                        type="number"
                        fieldName={'returning_customer_discount_days'}
                        isFieldUpdate
                        endAdornment={t('accountSettings.days')}
                      />
                    </Grid>
                    <Grid>
                      <Typography>{t('accountSettings.until')}</Typography>
                    </Grid>
                  </Grid>
                  <Grid item mt={2}>
                    <Grid
                      container
                      alignItems="center"
                      gap={0.5}
                      onClick={() => setApplicableOrderPagesOpen((isOpen) => !isOpen)}
                      sx={{ cursor: 'pointer', width: 'fit-content' }}>
                      <Typography fontWeight={700}>
                        {t('accountSettings.returningCustomerApplicableOrderPages')}
                      </Typography>
                      {/* The list is collapsed by default, so the header carries the selection state.
                          An empty selection means "all pages", so it gets the hint rather than "0/N". */}
                      <Typography variant="body2" color="text.secondary">
                        {values.returning_customer_discount_applicable_order_page_ids?.length
                          ? t('accountSettings.returningCustomerApplicableSelected', {
                              selected: values.returning_customer_discount_applicable_order_page_ids.length,
                              total: orderPagesCount,
                            })
                          : t('accountSettings.returningCustomerApplicableHint')}
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{
                          transform: applicableOrderPagesOpen ? 'rotate(180deg)' : undefined,
                          transition: 'transform 150ms',
                        }}>
                        <ExpandMore fontSize="small" />
                      </IconButton>
                    </Grid>
                    <Collapse in={applicableOrderPagesOpen} unmountOnExit>
                      <Typography variant="body2" color="text.secondary" mb={0.5}>
                        {t('accountSettings.returningCustomerApplicableHint')}
                      </Typography>
                      <OrderPagesMultiSelect
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        orderPagesFieldName="returning_customer_discount_applicable_order_page_ids"
                        account={account}
                      />
                    </Collapse>
                  </Grid>
                  {passOrderPages.length > 0 && (
                    <Grid item mt={2}>
                      <FormCheckbox
                        fieldName={'discount_applies_to_passes'}
                        disabled={false}
                        isFieldUpdate
                        label={t('accountSettings.returningCustomerAppliesToPasses')}
                      />
                      <Collapse in={values.discount_applies_to_passes} unmountOnExit>
                        <Grid item mt={1}>
                          <Typography fontWeight={700}>
                            {t('accountSettings.returningCustomerApplicablePasses')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" mb={0.5}>
                            {t('accountSettings.returningCustomerApplicableHint')}
                          </Typography>
                          <FormGroup>
                            {passOrderPages.map((page: PassOrderPageData) => (
                              <FormControlLabel
                                key={page.id}
                                control={
                                  <Checkbox
                                    name="returning_customer_discount_applicable_pass_order_page_ids"
                                    value={page.id}
                                    checked={
                                      values.returning_customer_discount_applicable_pass_order_page_ids?.includes(
                                        page.id,
                                      ) ?? false
                                    }
                                    onChange={handleChange}
                                  />
                                }
                                label={<Typography>{page.label[language]}</Typography>}
                              />
                            ))}
                          </FormGroup>
                        </Grid>
                      </Collapse>
                    </Grid>
                  )}
                </Grid>
              </Collapse>
              <Grid item my={3}>
                <Divider />
              </Grid>
              <Grid item>
                <Typography variant="h4">{t('accountSettings.abandonedCart')}</Typography>
              </Grid>
              <Grid item mb={2}>
                <Typography variant="body1" color="text.secondary">
                  {t('accountSettings.abandonedCartCaption')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  <MuiLink component={Link} to="/dashboard/email-templates#templates" underline="always">
                    {t('accountSettings.abandonedCartTemplateLink')}
                  </MuiLink>
                </Typography>
              </Grid>
              <Grid item>
                <FormCheckbox
                  fieldName={'should_send_abandoned_cart_notifications'}
                  disabled={false}
                  isFieldUpdate
                  label={t('accountSettings.enableAbandonedCartEmails')}
                />
              </Grid>
              {account.should_send_abandoned_cart_notifications_at && (
                <Grid container gap={1} flexWrap="nowrap" alignItems="center">
                  <Grid>
                    <Typography>{t('accountSettings.abandonedCartTime')}</Typography>
                  </Grid>
                  <Grid>
                    <Typography>
                      {moment(account.should_send_abandoned_cart_notifications_at)
                        .tz(account.timezone)
                        .format(`${longDateFormat} ${getTimeFormat(account)}`)}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              <Grid item my={3}>
                <Divider />
              </Grid>
              <Grid item>
                <Typography variant="h4">{t('accountSettings.externalVouchers')}</Typography>
              </Grid>
              <Grid item mb={2}>
                <Typography variant="body1" color="text.secondary">
                  {t('accountSettings.externalVouchersCaption')}
                </Typography>
              </Grid>
              <Grid item>
                <FormCheckbox
                  fieldName={'external_vouchers_enabled'}
                  disabled={false}
                  isFieldUpdate
                  label={t('accountSettings.enableExternalVouchers')}
                />
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" mt={3}>
              <Button type="submit" variant="contained" disabled={isUpdatingSettings}>
                {t('save')}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
